import axios from 'axios';

import { restUrl } from './utils';

// Bare axios instance for PUTs to signed GCS URLs: it must not inherit the
// interceptors installed on the default `axios` instance (e.g. the
// `Accept-Language` header), because GCS only accepts the CORS-safelisted
// headers plus `Content-Type` and rejects anything else on the preflight.
const gcsUpload = axios.create();

const UPLOADS_ENDPOINT = 'uploads';

// Consumers answer 422 (retry later) while the staged file is still being
// virus-scanned; keep retrying for about a minute before giving up.
const VIRUS_SCAN_PENDING_STATUS = 422;
const VIRUS_SCAN_MAX_RETRIES = 20;
const VIRUS_SCAN_RETRY_DELAY_MS = 3000;

interface CreatedUploadResponse {
    id: number;
    uploadUrl: string;
}

interface StageFileVariables {
    file: File;
    /** Called with the upload percentage in `[0, 100]` while the bytes are streamed. */
    onProgress?: (percent: number) => void;
    /** When aborted mid-flight, both `POST /uploads` and the follow-up PUT stop. */
    signal?: AbortSignal;
    /**
     * Invoked as soon as `POST /uploads` returns an id, before the PUT starts, so the caller can
     * DELETE the orphaned upload if the PUT is later aborted or fails.
     */
    onCreated?: (uploadId: number) => void;
}

interface StagedFile {
    /** Staged upload id from `POST /api/v1/uploads`. */
    uploadId: number;
}

const isAppEngineUploadUrl = (uploadUrl: string) => uploadUrl.includes('/api/v1/uploads/');

/** Generic upload staging: `POST /api/v1/uploads` then `PUT uploadUrl`; the id is consumed by a feature endpoint. */
async function stageFile({ file, onProgress, signal, onCreated }: StageFileVariables): Promise<StagedFile> {
    const contentType = file.type || 'application/octet-stream';

    const createResponse = await axios.post<CreatedUploadResponse>(
        restUrl(UPLOADS_ENDPOINT),
        { filename: file.name, size: file.size, contentType },
        { signal },
    );

    const { id: uploadId, uploadUrl } = createResponse.data ?? {};
    if (!uploadId || !uploadUrl) {
        throw new Error('Missing id or uploadUrl in POST /uploads response');
    }

    onCreated?.(uploadId);

    const putClient = isAppEngineUploadUrl(uploadUrl) ? axios : gcsUpload;

    await putClient.put(uploadUrl, file, {
        headers: { 'Content-Type': contentType },
        signal,
        onUploadProgress: (event) => {
            if (!onProgress) {
                return;
            }
            const total = event.total ?? file.size;
            if (!total) {
                return;
            }
            onProgress(Math.max(0, Math.min(100, Math.round((event.loaded / total) * 100))));
        },
    });

    return { uploadId };
}

async function deleteUpload(uploadId: number): Promise<void> {
    await axios.delete(`${restUrl(UPLOADS_ENDPOINT)}/${uploadId}`);
}

const isVirusScanPending = (error: unknown) =>
    axios.isAxiosError(error) && error.response?.status === VIRUS_SCAN_PENDING_STATUS;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Runs a consume call, retrying while the staged file is still being virus-scanned (HTTP 422). */
async function consumeWithVirusScanRetry<T>(consume: () => Promise<T>): Promise<T> {
    for (let attempt = 0; ; attempt++) {
        try {
            return await consume();
        } catch (error) {
            if (!isVirusScanPending(error) || attempt >= VIRUS_SCAN_MAX_RETRIES) {
                throw error;
            }
            await sleep(VIRUS_SCAN_RETRY_DELAY_MS);
        }
    }
}

export type { StagedFile, StageFileVariables };
export {
    consumeWithVirusScanRetry,
    deleteUpload,
    isAppEngineUploadUrl,
    isVirusScanPending,
    stageFile,
    VIRUS_SCAN_MAX_RETRIES,
    VIRUS_SCAN_PENDING_STATUS,
    VIRUS_SCAN_RETRY_DELAY_MS,
};
